/**
 * LLM 多帧 prompt 助手配置（管理后台）
 *
 * 与「智能拆分镜」对称：选择 LLM 模型 + system prompt + 计费 + 限频。
 */

import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import toast from '../../Toast';
import {
  directorAdminApi,
  type FrameStoryboardConfig,
} from '../../../apps/director/services/directorAdminApi';
import LlmModelPicker from '../llm/LlmModelPicker';

const DEFAULT_FORM: FrameStoryboardConfig = {
  frameStoryboardLlmEnabled: false,
  frameStoryboardLlmModelId: '',
  frameStoryboardLlmSystemPrompt: '',
  frameStoryboardCreditsPerCall: 3,
  frameStoryboardRateLimitPerMinute: 5,
};

export default function DirectorFrameStoryboardConfigTab() {
  const [form, setForm] = useState<FrameStoryboardConfig>(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    directorAdminApi
      .getFrameStoryboardConfig()
      .then((cfg) => setForm(cfg))
      .catch((err) => toast.error(err instanceof Error ? err.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const update = <K extends keyof FrameStoryboardConfig>(
    key: K,
    value: FrameStoryboardConfig[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const modelId = (form.frameStoryboardLlmModelId ?? '').trim();
    if (form.frameStoryboardLlmEnabled && modelId && !isUuid(modelId)) {
      toast.error('请重新选择 LLM 模型（当前值非合法模型 ID）');
      return;
    }
    setSaving(true);
    try {
      const patch: Partial<FrameStoryboardConfig> = {
        frameStoryboardLlmEnabled: form.frameStoryboardLlmEnabled,
        frameStoryboardLlmModelId: modelId || null,
        frameStoryboardLlmSystemPrompt: form.frameStoryboardLlmSystemPrompt,
        frameStoryboardCreditsPerCall: form.frameStoryboardCreditsPerCall,
        frameStoryboardRateLimitPerMinute: form.frameStoryboardRateLimitPerMinute,
      };

      await directorAdminApi.updateFrameStoryboardConfig(patch);
      toast.success('已保存');
      const fresh = await directorAdminApi.getFrameStoryboardConfig();
      setForm(fresh);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  function isUuid(v: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-light-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        加载中
      </div>
    );
  }

  return (
    <div className="card p-6 max-w-3xl space-y-5">
      <div>
        <h3 className="text-lg font-semibold mb-1">LLM 多帧 prompt 助手</h3>
        <p className="text-sm text-light-muted">
          在「批量生成多帧」弹窗里，让 AI 把一句话剧本拆成 N 帧 prompt（保持视觉连贯）。
        </p>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={form.frameStoryboardLlmEnabled}
          onChange={(e) => update('frameStoryboardLlmEnabled', e.target.checked)}
        />
        <span className="text-sm font-medium">启用 AI 多帧助手</span>
      </label>

      <div>
        <label className="block text-sm font-medium mb-1">LLM 模型</label>
        <LlmModelPicker
          value={form.frameStoryboardLlmModelId}
          onChange={(id) => update('frameStoryboardLlmModelId', id)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">System Prompt（可选）</label>
        <textarea
          value={form.frameStoryboardLlmSystemPrompt ?? ''}
          onChange={(e) => update('frameStoryboardLlmSystemPrompt', e.target.value)}
          rows={6}
          placeholder="留空则使用内置的默认多帧 prompt"
          className="w-full input text-sm font-mono"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">单次扣费积分</label>
          <input
            type="number"
            min={0}
            value={form.frameStoryboardCreditsPerCall}
            onChange={(e) =>
              update('frameStoryboardCreditsPerCall', parseInt(e.target.value, 10) || 0)
            }
            className="w-full input text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">每用户每分钟最多调用次数</label>
          <input
            type="number"
            min={1}
            value={form.frameStoryboardRateLimitPerMinute}
            onChange={(e) =>
              update('frameStoryboardRateLimitPerMinute', parseInt(e.target.value, 10) || 1)
            }
            className="w-full input text-sm"
          />
        </div>
      </div>

      <div className="pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary px-5 py-2 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          保存配置
        </button>
      </div>
    </div>
  );
}
